import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Pencil } from 'lucide-react';
import { useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import { api } from '@/lib/api';

interface SlotStatus {
  slotId: string;
  label: string;
  osId: string | null;
  plate: string | null;
}

const NO_SLOT = '';

/**
 * A vaga da OS no card do veículo, com a troca.
 *
 * O carro muda de elevador no meio do serviço; antes a vaga era a da abertura
 * para sempre, e o mapa de vagas e "Carros na oficina" mostravam o carro onde
 * ele não estava mais. A lista é a do mapa de vagas (só vagas de verdade, sem
 * a cabine de pintura): vaga com outro carro aparece com a placa e não pode
 * ser escolhida — a API recusa do mesmo jeito.
 *
 * OS encerrada não ocupa vaga (a conclusão libera), então ali só aparece a
 * vaga que ficou registrada, sem a troca.
 */
export function OSSlotField({
  osId,
  slotId,
  slotLabel,
  closed,
  onSave,
  isSaving,
}: {
  osId: string;
  slotId: string | null;
  slotLabel: string | null;
  closed: boolean;
  onSave: (slotId: string | null) => Promise<unknown>;
  isSaving: boolean;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState(NO_SLOT);

  const slotsQuery = useQuery<SlotStatus[]>({
    queryKey: ['slots-status'],
    queryFn: async () => (await api.get('/workshops/my/slots/status')).data.slots ?? [],
    enabled: editing,
  });

  if (closed && !slotLabel) return null;

  const start = () => {
    setChoice(slotId ?? NO_SLOT);
    setEditing(true);
  };

  const save = async () => {
    const next = choice === NO_SLOT ? null : choice;
    if (next === slotId) {
      setEditing(false);
      return;
    }
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível trocar a vaga',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  if (!editing) {
    return (
      <div>
        <p className="text-xs text-muted-foreground">Vaga</p>
        {closed ? (
          <p className="font-medium">{slotLabel}</p>
        ) : (
          <button
            type="button"
            onClick={start}
            className="group flex items-center gap-1.5 text-left"
            aria-label={slotLabel ? `Vaga ${slotLabel}. Trocar a vaga` : 'Escolher a vaga'}
          >
            <span className={slotLabel ? 'font-medium' : 'text-sm text-muted-foreground'}>
              {slotLabel ?? 'Sem vaga definida'}
            </span>
            <Pencil size={12} className="text-muted-foreground group-hover:text-foreground" aria-hidden />
          </button>
        )}
      </div>
    );
  }

  const slots = slotsQuery.data ?? [];

  return (
    <div className="col-span-2 space-y-2 rounded-md border p-3">
      <label htmlFor={`slot-${osId}`} className="text-xs font-medium text-muted-foreground">
        Vaga
      </label>
      {slotsQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" /> Carregando as vagas…
        </p>
      ) : slotsQuery.isError ? (
        <p className="text-sm text-muted-foreground">
          Não foi possível carregar as vagas.{' '}
          <button type="button" onClick={() => slotsQuery.refetch()} className="font-medium text-primary hover:underline">
            Tentar de novo
          </button>
        </p>
      ) : slots.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma vaga cadastrada.{' '}
          <Link to="/slots" className="font-medium text-primary hover:underline">
            Cadastrar vagas
          </Link>
        </p>
      ) : (
        <select
          id={`slot-${osId}`}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          className="block h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value={NO_SLOT}>Sem vaga definida</option>
          {slots.map((s) => {
            const taken = !!s.osId && s.osId !== osId;
            return (
              <option key={s.slotId} value={s.slotId} disabled={taken}>
                {taken ? `${s.label} — ocupada (${s.plate})` : s.label}
              </option>
            );
          })}
        </select>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={isSaving}
          className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={isSaving || slotsQuery.isLoading || slotsQuery.isError || slots.length === 0}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {isSaving && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Salvar
        </button>
      </div>
    </div>
  );
}
